/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useTtsGeneration.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing TTS generation and preview.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { AiProfile } from '../types';

/**
 * Hook for TTS generation
 */
export function useTtsGeneration() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const [isGeneratingTts, setIsGeneratingTts] = useState(false);
  const [ttsPreview, setTtsPreview] = useState<string | null>(null);

  const generateTTS = useCallback(
    async (text: string, aiProfile: AiProfile | null, onSuccess?: (url: string) => void) => {
      if (!aiProfile || !text.trim()) {
        showError(t('recordingManagement.errors.missingTtsData'));
        return null;
      }

      try {
        setIsGeneratingTts(true);

        // Clean up previous preview
        if (ttsPreview) {
          URL.revokeObjectURL(ttsPreview);
          setTtsPreview(null);
        }

        const response = await fetch(buildApiUrl('/api/telephony/recordings/generate-tts'), {
          method: 'POST',
          headers: {
            ...getApiHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text,
            ai_profile_id: aiProfile.id,
          }),
        });

        if (response.ok) {
          const audioBlob = await response.blob();
          const url = URL.createObjectURL(audioBlob);
          setTtsPreview(url);
          showSuccess(t('recordingManagement.messages.ttsGenerated'));
          onSuccess?.(url);
          return url;
        } else {
          const errorData = await response.json();
          showError(errorData.message || t('recordingManagement.errors.ttsGenerationFailed'));
          return null;
        }
      } catch (error) {
        showError(error.message || t('recordingManagement.errors.ttsGenerationFailed'));
        return null;
      } finally {
        setIsGeneratingTts(false);
      }
    },
    [showSuccess, showError, t, ttsPreview],
  );

  const clearPreview = useCallback(() => {
    if (ttsPreview) {
      URL.revokeObjectURL(ttsPreview);
      setTtsPreview(null);
    }
  }, [ttsPreview]);

  return {
    isGeneratingTts,
    ttsPreview,
    generateTTS,
    clearPreview,
  };
}

